import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';
import { useToasts } from 'common/toast';

import { Message, Dropdown, Pagination, Icon, Modal, Button } from 'semantic-ui-react';
import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { PageContent, PageHeader, PageSection } from 'common/styles/page';
import { StyledAddButton } from 'common/components/button/StyledButton';

import { organizationApi } from 'areas/settings/organization/api/organizationApi';
import { surveyStructureApi } from '../api/surveyStructureApi';
import { OrganizationRead } from 'store/generated/admin/organizations';

import OrganizationNewModal from './OrganizationNewModal';

const OrganizationManagement: React.FC = () => {
  const { addToast } = useToasts();
  const { setPathPageView } = useTrackingCode();
  const navigate = useNavigate();

  const [formValue, setFormValue] = useState<any>({
    organizationId: undefined,
    name: '',
    time_zone: 'US/Eastern',
    tier: undefined,
  });
  const [formErrors, setFormErrors] = useState<any>({ errors: 0 });
  const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [archiveOrgId, setArchiveOrgId] = useState<number | null>(null);
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const { data: organizations = [] } =
    organizationApi.useApiAdminV1OrganizationListQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });

  const [updateOrganization] =
    organizationApi.useApiAdminV1OrganizationPartialUpdateMutation();

  const [createOrganization] = organizationApi.useApiAdminV1OrganizationCreateMutation();

  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 9;

  const [nameFilter, setNameFilter] = useState('');

  const [sortConfig, setSortConfig] = useState<{
    key: keyof OrganizationRead;
    direction: 'asc' | 'desc';
  }>({
    key: 'name',
    direction: 'asc',
  });

  const handleSort = (key: keyof OrganizationRead) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const filteredOrganizations = useMemo(() => {
    const lowerName = nameFilter.trim().toLowerCase();

    const filtered = organizations.filter((org: any) => {
      const matchesArchived = org.is_archived === showArchived;
      const matchesName = !lowerName || org.name?.toLowerCase().startsWith(lowerName);
      return matchesArchived && matchesName;
    });

    return [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [organizations, showArchived, nameFilter, sortConfig]);

  const paginatedOrganizations = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredOrganizations.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredOrganizations, currentPage]);

  const totalPages = useMemo(
    () => Math.ceil(filteredOrganizations.length / PAGE_SIZE),
    [filteredOrganizations]
  );

  useEffect(() => {
    setPathPageView('/settings/organization');
  }, [setPathPageView]);

  const handleOrganizationSelect = (org: any) => {
    setFormValue({
      organizationId: org.id,
      name: org.name,
      time_zone: org.time_zone || 'US/Eastern',
      tier: org.tier,
    });
    setIsEditMode(true);
    setIsOrgModalOpen(true);
  };

  return (
    <PageContent data-testid="admin-organization-page">
      <PageSection>
        <PageHeader
          title={`Organization Management`}
          divider
          data-testid="admin-organization-header"
          controls={
            <StyledAddButton
              data-testid="admin-organization-new"
              onClick={() => navigate('/settings/admin/organization/create')}
              label="New Organization"
              width="150px"
              height="34px"
            />
          }
        />

        <PageSection display="flex" justifyContent="space-between" alignItems="center" padbottom>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              type="text"
              placeholder="Filter by name"
              value={nameFilter}
              onChange={e => {
                setNameFilter(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                height: '38px',
                border: '1px solid #ccc',
                borderRadius: '5px',
                padding: '0 10px',
                fontSize: '14px',
                minWidth: '300px',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Dropdown
              placeholder="Filter by status"
              selection
              compact
              value={showArchived ? 'archived' : 'active'}
              onChange={(_, data) => {
                setShowArchived(data.value === 'archived');
                setCurrentPage(1);
              }}
              options={[
                { key: 'active', text: 'Active', value: 'active' },
                { key: 'archived', text: 'Archived', value: 'archived' },
              ]}
              style={{
                height: '36px',
                border: '1px solid #ccc',
                borderRadius: '5px',
                fontSize: '14px',
                minWidth: '120px',
              }}
            />
          </div>
        </PageSection>

        <PageSection padbottom>
          <Table selectable data-testid="admin-organization-table">
            <TableHeader>
              <TableRow>
                <HeaderCell selectable onClick={() => handleSort('name')} width={6}>
                  Organization{' '}
                  {sortConfig.key === 'name' && (
                    <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                  )}
                </HeaderCell>
                <HeaderCell selectable onClick={() => handleSort('time_zone')} width={5}>
                  Time Zone{' '}
                  {sortConfig.key === 'time_zone' && (
                    <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                  )}
                </HeaderCell>
                <HeaderCell selectable onClick={() => handleSort('id')} width={2}>
                  ID{' '}
                  {sortConfig.key === 'id' && (
                    <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                  )}
                </HeaderCell>
                <HeaderCell width={2}>Status</HeaderCell>
                <HeaderCell width={1} justify="center">
                  Actions
                </HeaderCell>
              </TableRow>
            </TableHeader>

            <TableBody>
              {paginatedOrganizations.map((org: any) => (
                <TableRow key={org.id} $selectable onClick={() => navigate(`${org.id}/edit`)}>
                  <TableCell $firstcol width={6}>
                    {org.name}
                  </TableCell>
                  <TableCell width={5}>{org.time_zone || 'N/A'}</TableCell>
                  <TableCell width={2}>{org.id}</TableCell>
                  <TableCell>
                    <ValidationIcon>
                      <Icon
                        name={org.is_archived ? 'archive' : 'check circle'}
                        color={org.is_archived ? 'red' : 'green'}
                        title={org.is_archived ? 'Archived' : 'Active'}
                        size="large"
                      />
                      <span>{org.is_archived ? 'Archived' : 'Active'}</span>
                    </ValidationIcon>
                  </TableCell>
                  <TableCell width={1} justify="center">
                    <Dropdown
                      button
                      icon="ellipsis vertical"
                      floating
                      className="icon"
                      direction="left"
                      style={{ width: '38px', height: '35px', borderRadius: '8px' }}
                      onClick={e => e.stopPropagation()}
                    >
                      <Dropdown.Menu>
                        {showArchived ? (
                          <Dropdown.Item
                            text="Unarchive"
                            onClick={async e => {
                              e.stopPropagation();
                              try {
                                await updateOrganization({
                                  id: org.id,
                                  patchedOrganization: { is_archived: false },
                                }).unwrap();
                                addToast('Organization unarchived successfully', {
                                  appearance: 'success',
                                });
                              } catch (err) {
                                console.error('Failed to unarchive:', err);
                                addToast('Failed to unarchive organization', {
                                  appearance: 'error',
                                });
                              }
                            }}
                          />
                        ) : (
                          <>
                            <Dropdown.Item
                              text="Edit Organization"
                              onClick={e => {
                                e.stopPropagation();
                                handleOrganizationSelect(org);
                              }}
                            />
                            <Dropdown.Item
                              text="Archive Organization"
                              onClick={e => {
                                e.stopPropagation();
                                setArchiveOrgId(org.id);
                                setIsArchiveConfirmOpen(true);
                              }}
                            />
                          </>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <PageSection display="flex" justifyContent="end">
            <Pagination
              activePage={currentPage}
              totalPages={totalPages}
              onPageChange={(_, data) => setCurrentPage(data.activePage as number)}
              firstItem={null}
              lastItem={null}
              prevItem={{ content: '‹', icon: true }}
              nextItem={{ content: '›', icon: true }}
            />
          </PageSection>

          {formErrors?.name && <Message negative content={formErrors.name} />}
        </PageSection>
      </PageSection>

      <OrganizationNewModal
        open={isOrgModalOpen}
        isEdit={isEditMode}
        formValue={formValue}
        formErrors={formErrors}
        onChange={(field, value) => {
          setFormValue((prev: any) => ({ ...prev, [field]: value }));
          setFormErrors({ errors: 0 });
        }}
        onClose={() => setIsOrgModalOpen(false)}
        onSubmit={async () => {
          if (!formValue.name) {
            setFormErrors({ errors: 1, name: 'Organization name is required' });
            return;
          }

          try {
            if (isEditMode && formValue.organizationId) {
              await updateOrganization({
                id: formValue.organizationId,
                patchedOrganization: {
                  name: formValue.name,
                  time_zone: formValue.time_zone,
                  tier: formValue.tier,
                },
              }).unwrap();
              addToast('Organization updated successfully', { appearance: 'success' });
            } else {
              try {
                const result = await createOrganization({
                  organization: {
                    name: formValue.name,
                    time_zone: formValue.time_zone,
                    tier: formValue.tier,
                  },
                }).unwrap();

                /*
                TP: deprecated.
                                  await createSurveyStructure({
                                  surveyStructureCreate: {
                                    organization: result.id,
                                    name: formValue.name,
                                    status: 1,
                                    min_followup_questions: 2,
                                    max_followup_questions: 2,
                                  },
                                }).unwrap();
                 */
                addToast('Organization and survey structure created successfully', { appearance: 'success' });
                setIsOrgModalOpen(false);
              } catch (error) {
                console.error('Failed to create organization or survey structure:', error);
                addToast('Failed to create organization or survey structure', { appearance: 'error' });
              }

            }

            setIsOrgModalOpen(false);
          } catch (error) {
            console.error('Failed to save organization:', error);
            addToast('Failed to save organization', { appearance: 'error' });
          }
        }}
      />

      <Modal
        open={isArchiveConfirmOpen}
        onClose={() => setIsArchiveConfirmOpen(false)}
        size="small"
      >
        <Modal.Header>Confirm Archive</Modal.Header>
        <Modal.Content>
          <p>Are you sure you want to archive this organization?</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setIsArchiveConfirmOpen(false)}>Cancel</Button>
          <Button
            negative
            onClick={async () => {
              if (archiveOrgId) {
                try {
                  await updateOrganization({
                    id: archiveOrgId,
                    patchedOrganization: {
                      is_archived: true,
                    },
                  }).unwrap();
                  addToast('Organization archived successfully', { appearance: 'success' });
                } catch (err) {
                  console.error('Archive failed:', err);
                  addToast('Failed to archive organization', { appearance: 'error' });
                } finally {
                  setIsArchiveConfirmOpen(false);
                  setArchiveOrgId(null);
                }
              }
            }}
          >
            Archive
          </Button>
        </Modal.Actions>
      </Modal>
    </PageContent>
  );
};

export default OrganizationManagement;

const ValidationIcon = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem; /* Adjust spacing between icon and text */

  .ui.icon {
    margin: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  span {
    display: inline-flex;
    align-items: center;
  }
`;
